import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import api from "../api/api";


export default function LoginPage() {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [forceSignout, setForceSignout] = useState(true);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForceLogin, setShowForceLogin] = useState(false);
  const [forceData, setForceData] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState(null);
  const [forgotStatusLoading, setForgotStatusLoading] = useState(false);
  const [forgotCanRequestReset, setForgotCanRequestReset] = useState(false);
  const navigate = useNavigate();
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const loginRef = useRef(null);

  useEffect(() => {
    const getUrlParam = (paramName) => {
      const params = new URLSearchParams(window.location.search);
      for (const [name, value] of params.entries()) {
        if (name.toLowerCase() === paramName.toLowerCase()) {
          return value.trim();
        }
      }
      return "";
    };

    const loadDirectUser = async () => {
      const mdlDirect = getUrlParam("mdldirect");
      if (!mdlDirect) return false;

      sessionStorage.setItem("mdldirect", mdlDirect);
      sessionStorage.removeItem("mdldirectHandled");
      const usrId = mdlDirect.split("!")[0]?.trim();
      if (!usrId) return false;

      sessionStorage.setItem("UsrId", usrId);
      try {
        const user = await api.GetUserNameByUsrId(usrId);
        if (user?.usrName) {
          setUserName(user.usrName);
          setTimeout(() => passwordRef.current?.focus(), 100);
          return true;
        }
      } catch (err) {
        console.error("Failed to load direct-login user:", err);
        setMessage({ text: "Unable to load the user from the direct-login link.", type: "error" });
      }
      return false;
    };

    const details = sessionStorage.getItem("companyDetails");
    console.log("Details from sessionStorage:", details);
    if (details) {
      setCompanyDetails(JSON.parse(details));
      loadDirectUser().then((directUserLoaded) => {
        if (!directUserLoaded) {
          setTimeout(() => {
            usernameRef.current?.focus();
          }, 100);
        }
      });
    } else {
      navigate(`/${window.location.search}`, { replace: true });
    }
  }, [navigate]);

  const handleLogin = async () => {
    if (!userName || !password) {
      setMessage({ text: "Please enter username & password", type: "error" });
      return;
    }

    setLoading(true);

    try {
      const result = await api.LoginUser(userName, password, forceSignout ? 1 : 0);

      // ✅ Normalize response
      const loginData = result.checkLogin ?? result;

      if (loginData?.Message === "Login successful" || loginData?.Status === "0") {
        const user = loginData.UserData.Table[0];

        // ✅ Store session
        sessionStorage.setItem("userId", user.UsrId);
        sessionStorage.setItem("userName", user.UsrName);
        sessionStorage.setItem("LogId", loginData.LogId);
        sessionStorage.setItem("AuthToken", loginData.CurrentAuthToken);
        sessionStorage.setItem("UserInfo", JSON.stringify(user));
        //Local
        localStorage.setItem("userId", user.UsrId);
        localStorage.setItem("userName", user.UsrName);

        setPassword("");
        navigate("/explorer");
      } else if (loginData?.Message === "User already logged in" || loginData?.Status === "1") {
        setForceData(loginData); // store session info
        setShowForceLogin(true);

      }
      else if(loginData?.Status==="2"){
        setMessage({text: "Invalid Authentication Token",type:"error"});
      }
      else {
        setMessage({ text: loginData?.Message || "Login failed", type: "error" });
      }
    } catch (err) {
      setMessage({ text: "Login failed: " + err.message, type: "error" });
    }
    setLoading(false);
  };

  const resetForgotPasswordForm = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotEmail("");
    setForgotNewPassword("");
    setForgotOtp("");
    setForgotMessage(null);
    setForgotStatusLoading(false);
    setForgotCanRequestReset(false);
  };

  const handleForgotPasswordOpen = async () => {
    const resetUserName = userName || "admin";
    setShowForgotPassword(true);
    setForgotStep(1);
    setForgotMessage(null);
    setForgotCanRequestReset(false);
    setForgotStatusLoading(true);

    try {
      const result = await api.VerifyUserStatus(resetUserName);
      const userStatus = result?.data?.Table?.[0]?.Status;

      if (String(userStatus).toLowerCase() === "active") {
        setForgotCanRequestReset(true);
      } else {
        setForgotMessage({
          text: `The account for ${resetUserName} is inactive. Password reset is unavailable. Please contact IT Support for assistance.`,
          type: "error",
        });
      }
    } catch (err) {
      setForgotMessage({
        text: "Unable to verify the user account status. Please contact IT Support for assistance.",
        type: "error",
      });
    } finally {
      setForgotStatusLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (forgotStep === 1) {
      // Step 1: Request reset password
      if (!forgotCanRequestReset) {
        setForgotMessage({
          text: "Password reset is currently unavailable for this user. Please contact IT Support for assistance.",
          type: "error",
        });
        return;
      }
      if (!forgotEmail || !forgotNewPassword) {
        setForgotMessage({ text: "Please enter email and new password", type: "error" });
        return;
      }
      setForgotLoading(true);
      try {
        const result = await api.RequestResetPassword({ userName: userName || "admin", emailId: forgotEmail, newPassword: forgotNewPassword });
        setForgotMessage({ text: result.message || "OTP sent to your email", type: "success" });
        setForgotStep(2);
      } catch (err) {
        setForgotMessage({ text: "Request failed: " + err.message, type: "error" });
      }
      setForgotLoading(false);
    } else {
      // Step 2: Confirm with OTP
      if (!forgotOtp) {
        setForgotMessage({ text: "Please enter the OTP", type: "error" });
        return;
      }
      setForgotLoading(true);
      try {
        const result = await api.ConfirmResetPassword({ resetOtp: forgotOtp, userName: userName || "admin" });
        const message = typeof result === 'string' ? result : (result.message || "Password updated successfully");
        if (message.includes("Invalid or expired OTP")) {
          setForgotMessage({ text: message, type: "error" });
        } else {
          setForgotMessage({ text: message, type: "success" });
          setTimeout(() => {
            setShowForgotPassword(false);
            setForgotEmail("");
            setForgotNewPassword("");
            setForgotOtp("");
            setForgotMessage(null);
            setForgotStep(1);
          }, 2000);
        }
      } catch (err) {
        setForgotMessage({ text: "Confirmation failed: " + err.message, type: "error" });
      }
      setForgotLoading(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center w-screen h-screen">

      {/* Background Gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(135deg, var(--base-200), var(--base-100))",
        }}
      />

      {/* Soft Blobs */}
      <div
        className="absolute w-56 h-56 rounded-full blur-3xl opacity-15"
        style={{
          background: "var(--primary)",
          top: "12%",
          left: "8%",
        }}
      ></div>

      <div
        className="absolute rounded-full w-72 h-72 blur-3xl opacity-15"
        style={{
          background: "var(--primary-focus)",
          bottom: "8%",
          right: "12%",
        }}
      ></div>

      {/* Back Button */}
      <button
        onClick={() => navigate("/")}
        tabIndex="-1"
        className="absolute top-5 left-9 px-3 py-1.5 flex items-center gap-2 rounded-xl shadow-md
                  backdrop-blur-xl bg-white/20 hover:bg-white/30 transition-all border border-white/40"
        style={{ color: "var(--text-primary)", fontSize: "0.85rem" }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* LOGIN CARD */}
      <div className="relative z-10 w-full max-w-md px-6 py-4 glass-card animate-fade-in">

        {/* Logo */}
        <div className="flex justify-center mt-1 mb-3">
          <img
            src={companyDetails?.logoUrl}
            alt="Company Logo"
            className="object-cover h-20 shadow-lg w-23 rounded-xl"
          />
        </div>

        {/* Title */}
        <div className="mb-3 text-center">
          <h2
            className="text-2xl font-bold"
            style={{
              background: "linear-gradient(135deg, var(--primary), var(--primary-focus))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}> ERP Login </h2>

          {companyDetails?.companyDetails?.Table?.[0] && (
            <p className="mt-2 text-xl text-gray-1000">{companyDetails.companyDetails.Table[0].ComName}</p>
          )}
        </div>

        {/* Username */}
        <div className="mb-3">
          <label className="block mb-1 text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>
            Username
          </label>
          <input
            ref={usernameRef}
            className="h-10 pl-10 text-sm input-professional"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                passwordRef.current?.focus();
              }
            }}
            placeholder="Enter username"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
          />
        </div>

        {/* Password */}
        <div className="mb-3">
          <label className="block mb-1 text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>
            Password
          </label>
          <div className="relative">
            <input
              ref={passwordRef}
              className="h-10 pl-10 pr-10 text-sm input-professional"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Tab" && !e.shiftKey) {
                  e.preventDefault();
                  handleLogin();
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  loginRef.current?.focus();
                }
              }}
              placeholder="Enter password"
              autoComplete="off"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((visible) => !visible)}
              className="absolute inset-y-0 right-0 flex items-center px-3"
              style={{ color: "var(--text-secondary)" }}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-900 cursor-pointer">
          <input
            type="checkbox"
            tabIndex={-1}
            checked={forceSignout}
            onChange={(e) => setForceSignout(e.target.checked)}
          />
          <span>Sign out any existing session for this user</span>
        </label>

        {/* Login Button */}
        <button
          ref={loginRef}
          onClick={handleLogin}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleLogin();
            }
          }}
          disabled={loading}
          className="w-full mt-1 text-base btn-professional h-11"
        >
          {loading ? "Loading..." : "Sign In"}
        </button>

        {/* Message */}
        {message && (
          <div
            className={`mt-3 p-2 rounded-lg text-center text-sm ${message.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
              }`}
          >
            {message.text}
          </div>
        )}

        {/* Bottom Links */}
        <div className="mt-4 text-center">
          <p
            className="text-sm font-medium text-blue-600 cursor-pointer hover:underline"
            onClick={handleForgotPasswordOpen}
          >
            Forgot Password?
          </p>
          <p className="mt-1 text-xs text-gray-600" style={{ color: "var(--text-secondary)" }}>
            Need help? Contact IT Support
          </p>
        </div>

      </div>
      {showForceLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-full max-w-md p-5 bg-white border border-gray-200 shadow-2xl rounded-2xl animate-fade-in">

            <h3 className="mb-2 text-xl font-bold text-center text-red-600">
              Active Session Detected
            </h3>

            <p className="mb-4 text-sm text-center text-gray-600">
              Your account is currently logged in on another device or browser.
              For security reasons, only one active session is allowed.
            </p>

            <div className="p-3 mb-4 text-sm border border-yellow-300 rounded-lg bg-yellow-50">
              <strong>Last Login:</strong><br />
              IP: {forceData?.SessionData?.Table?.[0]?.IPAddress}<br />
              Time: {forceData?.SessionData?.Table?.[0]?.LogInTime}<br/>
              User: {forceData?.SessionData?.Table?.[0]?.UsrName}

            </div>

            <div className="flex justify-end gap-2 mt-4">
            <button
  onClick={() => {
    const authToken = forceData?.CurrentAuthToken || forceData?.SessionData?.Table?.[0]?.AuthToken;
    navigate("/forcelogin", {
      state: { authToken },
    });
  }}
  className="px-4 py-2 text-sm text-white transition bg-red-600 rounded-lg hover:bg-red-700"
>
  Force Sign In
</button>
              <button
                onClick={() => setShowForceLogin(false)}
                className="px-4 py-2 text-sm transition border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <>
          <div className="fixed top-0 bottom-0 left-0 right-0 z-40 bg-black/40 backdrop-blur-md" onClick={resetForgotPasswordForm}></div>
          <div className="fixed top-0 bottom-0 left-0 right-0 z-50 flex items-center justify-center p-4" onClick={resetForgotPasswordForm}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white border border-gray-300 rounded-xl p-6 max-w-md max-h-[80vh] overflow-y-auto shadow-xl">
              <h3 className="mb-4 text-xl font-semibold text-base-content">Forgot Password</h3>
              <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <span className="font-medium text-gray-900">Username:</span> {userName || "admin"}
              </div>
              {forgotStatusLoading && (
                <div className="mb-4 p-2 rounded-md text-sm bg-blue-50 text-blue-700">
                  Verifying user account status...
                </div>
              )}
              {forgotStep === 1 ? (
                <>
                  <div className="mb-4">
                    <label className="block mb-1 text-sm font-medium text-base-content">Email ID</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block mb-1 text-sm font-medium text-base-content">New Password</label>
                    <input
                      type="password"
                      value={forgotNewPassword}
                      onChange={(e) => setForgotNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                      placeholder="Enter new password"
                    />
                  </div>
                </>
              ) : (
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-base-content">Enter OTP</label>
                  <input
                    type="text"
                    value={forgotOtp}
                    onChange={(e) => setForgotOtp(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                    placeholder="Enter the OTP sent to your email"
                  />
                </div>
              )}
              {forgotMessage && (
                <div className={`mb-4 p-2 rounded-md text-sm ${forgotMessage.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                  {forgotMessage.text}
                </div>
              )}
              <div className="flex justify-end gap-2">
               
                <button
                  onClick={handleForgotPassword}
                  disabled={forgotLoading || forgotStatusLoading || (forgotStep === 1 && !forgotCanRequestReset)}
                  className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-green-500 border border-green-600 rounded-md hover:bg-green-600 hover:border-green-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                >
                  {forgotStatusLoading ? "Verifying..." : (forgotLoading ? (forgotStep === 1 ? "Requesting..." : "Confirming...") : (forgotStep === 1 ? "Request Reset" : "Confirm Reset"))}
                </button>
                 <button
                  onClick={resetForgotPasswordForm}
                  className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-red-500 border border-red-600 rounded-md hover:bg-red-600 hover:border-red-700 hover:shadow-md hover:scale-105"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
